import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { Label, Stack } from '@bingads-webui-react/fabric-control';
import { ResponsiveAd } from '@bingads-webui/campaign-odata-schemas';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { ValidationGroup, withCustomValidation, withError } from '@bingads-webui-react/validation';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { ValidatedFinalUrl as UrlInput } from '@bingads-webui-campaign-react/validated-url-input';
import { IconButton } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { ThirdPartyMeasurementContainerWithValidation } from '@bingads-webui-campaign-react/third-party-measurement';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { AdvancedSettings } from '@bingads-webui-campaign-react/display-plus-native-ads';

import { ValidatedImageExperience } from './validated-image-experience';
import { getAdsStepStyles } from './styles';

const ValidatedFinalUrl = withControlErrorHandling(withUnvalidatedControlErrors(withError(withCustomValidation(UrlInput))));

export const DisplayAdPanel = ({
  i18n,
  perfMarker,
  cid,
  aid,
  campaignId,
  onUpdate,
  onRemovePanel,
  removeCloseButton,
  permissions,
  displayAd,
  keyIndex,
  isSelectedPanel,
  oDataConfig,
  customStyleFunction,
  useCustomStyleFunction,
  isLoadingImage,
}) => {
  const defaultClasses = useDefaultStyles(getAdsStepStyles);

  const customClasses = useDefaultStyles(customStyleFunction);

  const classes = useCustomStyleFunction ? customClasses : defaultClasses;

  const legacyI18n = useMemo(() => window.globalLegacyI18n.get('$/i18n!legacy/global'), []);
  const i18nMerged = useMemo(() => mergeI18n(legacyI18n, i18n), [legacyI18n, i18n]);
  const finalUrlsObj = { FinalUrls: displayAd && displayAd.FinalUrls };

  const verifiedTrackingSettingObj = {
    VerifiedTrackingSettings: displayAd && displayAd.VerifiedTrackingSettings,
  };

  const isIasTrackingEnable = _.result(permissions.dynamic, 'EnableDisplayAdsIasTracking', false);
  const isAdvancedURLOptionsForMSANDisplayEnabled =
    _.result(permissions.dynamic, 'AdvancedURLOptionsForDisplay', false) &&
    _.result(permissions.dynamic, 'EnableMSANDisplayCampaigns', false);

  const isImpressionTrackingForDisplayAndVideoEnabled =
    _.result(permissions, 'isImpressionTrackingForDisplayAndVideoEnabled', false);

  const IsMOATEnabled = _.result(permissions, 'IsMOATEnabled', false);

  const onValidationUpdate = useCallback((result) => {
    onUpdate(result);
  }, [onUpdate]);

  const advancedSettings = useMemo(() => (
    <AdvancedSettings
      trackingUrlTemplate={displayAd && displayAd.TrackingUrlTemplate}
      finalUrlSuffix={displayAd && displayAd.FinalUrlSuffix}
      urlCustomParameters={displayAd && displayAd.UrlCustomParameters}
      impressionTrackingUrls={displayAd && displayAd.ImpressionTrackingUrls}
      verifiedTrackingSettings={verifiedTrackingSettingObj.VerifiedTrackingSettings}
      isDisplay
      isVideo={false}
    />
  ), [verifiedTrackingSettingObj.VerifiedTrackingSettings, displayAd]);

  return (
    <div className={classNames([classes.displayAdPanelRoot, isSelectedPanel ? classes.selectedPanel : '', 'display-ad-panel-root'])}>
      <ValidationGroup
        validationProps={{
          schema: ResponsiveAd,
          onValidate: onValidationUpdate,
        }}
        key={`${keyIndex}-validationGroup`}
      >
        <Stack tokens={{ childrenGap: 16 }}>
          { !removeCloseButton &&
            <div className={classes.displayAdPanelHeader}>
              <IconButton iconProps={{ iconName: 'Cancel' }} title="Remove ad" className="remove-ad-panel" onClick={onRemovePanel} />
            </div>
          }
          <ValidatedFinalUrl
            containerClassName={classNames(classes.displayAdPanelFinalUrlContainer, 'final-url-container')}
            i18n={i18n}
            validationProps={{
              data: finalUrlsObj,
              initialValue: finalUrlsObj,
            }}
            validateOnRender
          />
          <div>
            <div className={classes.displayImageLabel}>
              <Label>
                <Stack horizontal verticalAlign="center" >
                  {i18n.getString(_TL_('Image'))}
                  <HelpPopupInline topic="pop_BA_Display_Image" />
                </Stack>
              </Label>
            </div>
            <ValidatedImageExperience
              i18n={i18nMerged}
              perfMarker={perfMarker}
              permissions={permissions}
              oDataConfig={oDataConfig}
              cid={cid}
              aid={aid}
              campaignId={campaignId}
              disableRemoveAll
              hideRatioLabel
              displayAd={displayAd}
              key={`${keyIndex}-ImageExperience`}
              idx={keyIndex}
              ImageExperienceCircleUploadType
              disableUploadTextField
              ImagePickerContainerHeight="auto"
              isLoadingImage={isLoadingImage}
            />
          </div>
          { !(isAdvancedURLOptionsForMSANDisplayEnabled || isImpressionTrackingForDisplayAndVideoEnabled)
          && isIasTrackingEnable && (
          <ThirdPartyMeasurementContainerWithValidation
            i18n={i18n}
            videoAd={displayAd}
            validationProps={{
                  data: verifiedTrackingSettingObj,
                  initialValue: verifiedTrackingSettingObj,
                }}
            adType="display"
            IsMOATEnabled={IsMOATEnabled}
          />
          )}
          { (isAdvancedURLOptionsForMSANDisplayEnabled || isImpressionTrackingForDisplayAndVideoEnabled) && (
            <div>
              {advancedSettings}
            </div>
          )}
        </Stack>
      </ValidationGroup>
    </div>
  );
};

DisplayAdPanel.propTypes = {
  perfMarker: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      EnableDisplayAdsIasTracking: PropTypes.bool,
    }),
  }).isRequired,
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
  campaignId: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemovePanel: PropTypes.func.isRequired,
  removeCloseButton: PropTypes.bool.isRequired,
  displayAd: PropTypes.shape([]).isRequired,
  keyIndex: PropTypes.number.isRequired,
  isSelectedPanel: PropTypes.bool.isRequired,
  oDataConfig: PropTypes.shape({}),
  customStyleFunction: PropTypes.func,
  useCustomStyleFunction: PropTypes.bool,
  isLoadingImage: PropTypes.bool.isRequired,
};

DisplayAdPanel.defaultProps = {
  oDataConfig: null,
  customStyleFunction: _.noop,
  useCustomStyleFunction: false,
};
